import { Users } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * "2 alumnos" — the signal that one conversation is really two enrolments.
 *
 * One component for the Buzón row and the Kanban card so the two can never
 * disagree about the threshold or the wording. Both read a `candidateCount`
 * their own list query computes; neither loads the people themselves.
 *
 * **Renders nothing below 2.** One candidate says nothing the card does not
 * already imply, and zero is the ordinary case of a prospect enrolling
 * themselves — a "1 alumno" chip on most rows would be noise that teaches the
 * eye to skip the chip entirely, which is exactly what the 2+ case needs it not
 * to do.
 *
 * Brand lime rather than the amber of "Esperando respuesta" or the grey of "IA
 * pausada": those two mean attention and muted state. This one is opportunity,
 * and it should not read as either.
 *
 * The plan gate is NOT here: `candidateCount` arrives already zeroed for a client
 * whose plan lacks `enrollment` (the same read-side gating `settingsService.getLogoUrl`
 * does for the logo). Gating in this component instead would drag the features
 * query into every list that renders a row, and would let the Buzón and the
 * Kanban disagree the moment one of them forgot to pass it.
 */
export function CandidateCountChip({
  count,
  className,
}: {
  count: number;
  className?: string;
}): React.JSX.Element | null {
  // `Number.isFinite`, not `count < 2`: the field crosses a version boundary,
  // and a CRM bundle that deploys ahead of the backend receives `undefined`.
  // `undefined < 2` is FALSE, so the naive guard let it through and rendered a
  // chip reading " alumnos" with no number.
  if (!Number.isFinite(count) || count < 2) return null;
  return (
    <span
      title={`${count} personas registradas en este prospecto`}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full bg-lime-pale px-2 py-0.5 text-[10px] font-semibold text-moss",
        className,
      )}
    >
      <Users className="h-2.5 w-2.5" />
      {count} alumnos
    </span>
  );
}
